/**
 * useOutputFilenameSuggestion — the ONE place the output-file version probe
 * lives (sub-task 03 R1.4/R1.9, packet 3.1).
 *
 * **No new endpoint.** `<N>` comes from the existing `GET /imports` through
 * the existing `useImportsList` hook, with the R1.4 params:
 * `{ owner: "all", tag: null, q: "<stem>_AI_review_v", page: 1, pageSize: 100 }`.
 *
 * - `owner: "all"` is mandatory, not a widening for convenience: another
 *   recruiter may have produced v1 of this import, and a `you`-scoped probe
 *   would re-mint their version number.
 * - `q` is a case-insensitive substring the server matches over
 *   `original_filename` OR `search_name_cached`
 *   (`app/web/v2/imports/repository.py:249-262`), so the page it returns is a
 *   superset. `outputFilename.ts` re-filters it with a stem-anchored regex on
 *   `filename` — never on `search_name`.
 * - `page_size` is server-capped at 100 (`imports/router.py:325`). Past that
 *   the page is a partial view: `truncated` says so, and R1.9 makes the caller
 *   suppress the duplicate warning rather than render a false negative. The
 *   suggestion stays `max + 1`.
 *
 * The probe is ADVISORY throughout: an error degrades to the `v1` fallback
 * (F2) and never surfaces a banner or blocks the run.
 */

import type { ImportsClient } from "../../../clients/imports.client";
import { useImportsList } from "../imports/useImportsList";
import { reviewProbeQuery, suggestOutputFilenameFromRows } from "./outputFilename";

/**
 * The server's own cap (`imports/router.py:325`) — asking for more is a 422.
 *
 * The endpoint's cap, i.e. the FIRST 100 in one request — not a page size and
 * not "everything": deliberately NOT `listPageSize()` from
 * `@nu-assist/shared-fe`, which resolves per stage (20 local / 50 dev / 100
 * prod) for the three visible lists. The probe scans prior review outputs to
 * pick the next `_v<N>`; paging it down to 20 would make it miss higher
 * versions and suggest a filename that already exists. The probe is
 * stem-filtered (`q: "<stem>_AI_review_v"`), so the same miss exists at 100
 * only once a SINGLE parent import has more than 100 review outputs — and
 * then it is surfaced, not silent: the hook reports `truncated` and R1.9
 * suppresses the duplicate warning rather than render a false negative. A
 * known limit of a single-page probe, not something the resolver would fix.
 */
const PROBE_PAGE_SIZE = 100;

export interface UseOutputFilenameSuggestionArgs {
  client: ImportsClient;
  /** The selected import's filename, or `null` when nothing is selected. */
  parentFilename: string | null;
}

export interface OutputFilenameSuggestion {
  /** `<stem>_AI_review_v<N>.csv`, or `null` until the probe has settled FOR
   *  THIS parent (never a value derived from the previous import's page). */
  suggestion: string | null;
  /** Every filename on the probe page — the duplicate check's haystack. */
  existingFilenames: string[];
  /** More matches exist than the page returned (R1.9). */
  truncated: boolean;
}

export function useOutputFilenameSuggestion({
  client,
  parentFilename,
}: UseOutputFilenameSuggestionArgs): OutputFilenameSuggestion {
  const enabled = parentFilename !== null;
  const query = useImportsList({
    client,
    owner: "all",
    tag: null,
    q: parentFilename ? reviewProbeQuery(parentFilename) : "",
    // The probe asks "which review files of THIS import already exist" — it is
    page: 1,
    pageSize: PROBE_PAGE_SIZE,
    enabled,
  });

  // `useImportsList` keeps the previous page visible across a key change
  // (`placeholderData: (prev) => prev`). That is right for the imports table
  // and WRONG here: seeding the field from the previous import's versions
  // would stick, since the page only re-seeds once per import. Treat
  // placeholder data as "not settled yet" and emit `null`.
  const settled = enabled && !query.isPlaceholderData && (query.isSuccess || query.isError);
  const data = settled ? query.data : undefined;
  const rows = data?.items ?? [];
  // The duplicate check (R1.5) is name-only over the WHOLE page: a clashing
  // name is a clashing name whoever's child it is. The version count is now
  // name-scoped too (see `suggestOutputFilenameFromRows`) — chaining made a
  // lineage-scoped max mint names that already existed — so the two agree
  // again instead of the count being the narrower of the pair.
  const existingFilenames = rows.map((item) => item.filename);

  return {
    suggestion:
      settled && parentFilename ? suggestOutputFilenameFromRows(parentFilename, rows) : null,
    existingFilenames,
    truncated: data ? data.total > data.items.length : false,
  };
}
